/**
 * Claim-id resolution for the edit flow.
 *
 * Deliberately site-local rather than in commonAssets: it changes how a claim
 * is dispatched, and only this site and SunPower are meant to be affected.
 * www.sunpowersecuritiessettlement.com/scripts/resolveClaimId.js is an
 * intentional copy -- keep the two in step.
 *
 * An edit has to go out as `PUT /claims`. That routes to putClaim, which
 * archives to claim_history / claim_field_history /
 * claim_field_transactional_history and then DELETEs and re-INSERTs -- correct
 * replace semantics.
 *
 * A POST routes to postClaim instead, whose
 *
 *     INSERT INTO claim ... ON DUPLICATE KEY UPDATE ...
 *
 * violates the unique index `idx_unique_zero_zero_id_domain` on
 * (zero_zero_id, domain). Its catch swallows the error and returns early, so
 * claim_field, claim_field_transactional and class_member are never written --
 * the edit is dropped entirely while the browser still shows success, because
 * postClaimV4 already returned the correlation_id.
 *
 * 36 edits were lost that way between 2026-07-09 and 2026-08-06.
 */

/**
 * Resolve the id of the claim currently being edited.
 *
 * post_claim used to depend on `claim.data.claim_id` alone. The class member
 * lookup response is also kept on `claim.class_member`, and getClassMember
 * nests the raw class_member row inside it, so read all three.
 *
 * @param {Object} claim - The claim object (`this.claim`, or the copy
 *   customProcessClaim returns).
 * @returns {string|number|null} The claim id, or null when none is available.
 */
export function resolveClaimId(claim) {
    if (!claim) return null;

    const member = claim.class_member;
    const candidates = [
        claim.data && claim.data.claim_id,
        member && member.claim_id,
        // getClassMember sets claim[0].class_member = class_member[0] when an
        // editable claim exists, so the class member's own column lives here.
        member && member.class_member && member.class_member.claim_id,
    ];

    for (const candidate of candidates) {
        const claimId = normalizeClaimId(candidate);
        if (claimId !== null) return claimId;
    }

    return null;
}

/**
 * Did this class member lookup return an EXISTING claim?
 *
 * getClassMember answers with the `claim` row -- with the class member row
 * nested inside it -- only when the member already has an editable claim. A
 * member with no claim gets the bare class_member row back, which carries
 * none of these. That distinction is what keeps the generic / new-claim path
 * completely untouched: it can never look like an edit.
 *
 * @param {Object} response - The classMemberPost response data.
 * @returns {boolean} True when the response describes an existing claim.
 */
export function isExistingClaimResponse(response) {
    if (!response || typeof response !== "object") return false;

    return (
        // Only ever attached to a claim row.
        Boolean(response.class_member) ||
        // claim.correlation_id; not a class_member column.
        Boolean(response.correlation_id) ||
        normalizeClaimId(response.claim_id) !== null
    );
}

/**
 * Reduce a claim id to something the API can match on.
 *
 * postClaim CONCATs onto `class_member.claim_id`, so that column can hold a
 * comma list ("164597606,164597607") rather than a single id. putClaim binds
 * the value into `WHERE id = ?`, where MySQL coerces such a string to the
 * FIRST id and silently updates the wrong claim. Take the last entry -- the
 * most recently filed one -- which is the claim the edit portal loaded.
 *
 * A single numeric id is returned unchanged, including its type, so the common
 * case puts exactly the same value on the wire as before.
 *
 * @param {string|number|null|undefined} value - The raw claim id.
 * @returns {string|number|null} A single claim id, or null when there is none.
 */
export function normalizeClaimId(value) {
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "number")
        return Number.isInteger(value) && value > 0 ? value : null;

    const last = String(value)
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
        .pop();

    if (!last || !/^\d+$/.test(last) || Number(last) <= 0) return null;

    return last;
}
